// Iter 75/76 — Vendor Bill detail page with embedded Payments panel.
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, FileText, Plus, FileArrowUp, DownloadSimple,
    Trash, Receipt, CurrencyInr,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    BILL_STATUS_LABEL, BILL_STATUS_BADGE,
    TDS_LABEL, DOC_TYPE_LABEL, humanBytes, PAYMENT_MODES,
    formatAmount,
} from "./vendor_constants";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function VendorBillDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { can } = useAuth();
    const [bill, setBill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const [payOpen, setPayOpen] = useState(false);
    const [editPayment, setEditPayment] = useState(null);
    const [docOpen, setDocOpen] = useState(false);

    const reload = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        let cancelled = false;
        api.get(`/vendor-bills/${id}`)
            .then(({ data }) => { if (!cancelled) setBill(data); })
            .catch((err) => {
                if (!cancelled) {
                    toast.error(formatApiError(err, "Failed to load bill"));
                    navigate("/vendor-bills");
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [id, navigate, reloadKey]);

    const cancelBill = async () => {
        const reason = window.prompt("Reason for cancelling this bill:");
        if (reason === null) return;
        try {
            await api.patch(`/vendor-bills/${id}/status`, { status: "cancelled", reason: reason || "" });
            toast.success("Bill cancelled");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Cancel failed"));
        }
    };

    const removeDoc = async (doc) => {
        if (!window.confirm(`Delete '${doc.file_name}'?`)) return;
        try {
            await api.delete(`/vendor-bills/${id}/documents/${doc.id}`);
            toast.success("Document deleted");
            reload();
        } catch (err) { toast.error(formatApiError(err, "Delete failed")); }
    };

    const deletePayment = async (p) => {
        if (!window.confirm(`Delete payment ${p.code} (${formatAmount(p.amount, bill.currency)})?`)) return;
        try {
            await api.delete(`/vendor-payments/${p.id}`);
            toast.success("Payment deleted");
            reload();
        } catch (err) { toast.error(formatApiError(err, "Delete failed")); }
    };

    if (loading) return <div className="px-8 py-12 text-slate-500" data-testid="vbill-detail-loading">Loading…</div>;
    if (!bill) return null;

    const badge = BILL_STATUS_BADGE[bill.status] || BILL_STATUS_BADGE.pending;
    const locked = ["paid", "cancelled"].includes(bill.status);
    const canPay = can("vendor:write") && bill.status !== "paid" && bill.status !== "cancelled";

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1300px]" data-testid="vbill-detail-page">
            <PageHeader
                eyebrow={`${bill.code} · ${bill.vendor_code} ${bill.vendor_name}`}
                title={`Bill ${bill.bill_number}`}
                description={
                    <span className="inline-flex items-center gap-3 flex-wrap mt-1">
                        <Badge data-testid="vbill-status-badge" className={`${badge.className} hover:${badge.className}`}>
                            {badge.label}
                        </Badge>
                        <span className="text-xs text-slate-500">
                            {bill.currency} · Bill date {formatDate(bill.bill_date)} · Due {formatDate(bill.due_date)}
                        </span>
                        {bill.vendor_purchase_order_code && (
                            <Badge variant="secondary" className="bg-sky-50 text-sky-700 hover:bg-sky-50">
                                From PO {bill.vendor_purchase_order_code}
                            </Badge>
                        )}
                        {!bill.vendor_purchase_order_code && (
                            <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100">
                                Standalone
                            </Badge>
                        )}
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button data-testid="vbill-back-btn" variant="outline" onClick={() => navigate(-1)} className="gap-1">
                            <ArrowLeft size={14} /> Back
                        </Button>
                        {bill.vendor_purchase_order_id && (
                            <Button data-testid="vbill-open-po-btn" variant="outline"
                                onClick={() => navigate(`/vendor-purchase-orders/${bill.vendor_purchase_order_id}`)}
                                className="gap-1"><FileText size={14} /> Source PO</Button>
                        )}
                        {canPay && (
                            <Button data-testid="vbill-record-payment-btn" size="sm"
                                onClick={() => { setEditPayment(null); setPayOpen(true); }} className="gap-1">
                                <CurrencyInr size={14} /> Record Payment
                            </Button>
                        )}
                        {can("vendor:write") && bill.status !== "cancelled" && bill.status !== "paid" && (
                            <Button data-testid="vbill-cancel-btn" size="sm" variant="outline"
                                onClick={cancelBill} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                                Cancel Bill
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-5">
                    {/* Financials summary */}
                    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vbill-summary">
                        <h3 className="font-medium text-slate-900 mb-4">Bill Summary</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <Stat label="Subtotal" value={formatAmount(bill.subtotal, bill.currency)} />
                            <Stat label="GST" value={formatAmount(bill.gst_amount, bill.currency)} />
                            <Stat label="Gross" value={formatAmount(bill.gross_amount, bill.currency)} />
                            <Stat label="Net Payable" value={formatAmount(bill.net_payable, bill.currency)} tone="positive" testid="vbill-net-payable" />
                        </div>
                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <Stat label="TDS Section" value={TDS_LABEL[bill.tds_section] || bill.tds_section || "—"} />
                            <Stat label="TDS Rate" value={`${Number(bill.tds_rate || 0).toFixed(2)}%`} />
                            <Stat label={`TDS Amount${bill.tds_override ? " (override)" : ""}`} value={formatAmount(bill.tds_amount, bill.currency)} testid="vbill-tds-amount" />
                            <Stat label="Currency" value={bill.currency} />
                        </div>
                        {bill.description && (
                            <div className="mt-4 text-xs text-slate-600">
                                <span className="text-slate-400">Description:</span> {bill.description}
                            </div>
                        )}
                    </div>

                    {/* Payments panel — embedded */}
                    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vbill-payments-panel">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium text-slate-900">Payments</h3>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">
                                {bill.payment_count || 0} payment(s)
                            </Badge>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4 p-4 rounded-md bg-slate-50">
                            <Stat label="Bill Amount" value={formatAmount(bill.net_payable, bill.currency)} testid="vbill-summary-amount" />
                            <Stat label="Paid" value={formatAmount(bill.amount_paid, bill.currency)} tone="positive" testid="vbill-summary-paid" />
                            <Stat label="Outstanding" value={formatAmount(bill.outstanding_amount, bill.currency)} tone={bill.outstanding_amount > 0 ? "negative" : "positive"} testid="vbill-summary-outstanding" />
                        </div>
                        {(bill.payments || []).length === 0 ? (
                            <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-md" data-testid="vbill-payments-empty">
                                No payments recorded
                                {canPay && (
                                    <div className="text-xs text-slate-400 mt-1">Click &apos;Record Payment&apos; to add the first.</div>
                                )}
                            </div>
                        ) : (
                            <div className="overflow-x-auto" data-testid="vbill-payments-table">
                                <table className="w-full text-sm">
                                    <thead className="text-slate-600 text-xs">
                                        <tr className="border-b border-slate-200">
                                            <th className="text-left px-3 py-2 font-medium">Code</th>
                                            <th className="text-left px-3 py-2 font-medium">Date</th>
                                            <th className="text-left px-3 py-2 font-medium">Mode</th>
                                            <th className="text-left px-3 py-2 font-medium">Reference</th>
                                            <th className="text-right px-3 py-2 font-medium">Amount</th>
                                            <th className="text-left px-3 py-2 font-medium">Remarks</th>
                                            <th className="w-10" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bill.payments.map((p) => (
                                            <tr key={p.id} className="border-b border-slate-100" data-testid={`vbill-payment-${p.code}`}>
                                                <td className="px-3 py-2 font-mono text-xs">{p.code}</td>
                                                <td className="px-3 py-2 text-xs text-slate-700">{formatDate(p.payment_date)}</td>
                                                <td className="px-3 py-2 text-xs">{p.payment_mode}</td>
                                                <td className="px-3 py-2 text-xs text-slate-600 font-mono">{p.reference_number || "—"}</td>
                                                <td className="px-3 py-2 text-right font-mono">{formatAmount(p.amount, bill.currency)}</td>
                                                <td className="px-3 py-2 text-xs text-slate-500">{p.remarks || "—"}</td>
                                                <td className="px-3 py-2 text-right">
                                                    {can("vendor:write") && bill.status !== "cancelled" && (
                                                        <div className="flex items-center gap-1 justify-end">
                                                            <button
                                                                data-testid={`vbill-payment-edit-${p.code}`}
                                                                onClick={() => { setEditPayment(p); setPayOpen(true); }}
                                                                className="text-xs text-indigo-600 hover:underline"
                                                            >Edit</button>
                                                            <button
                                                                data-testid={`vbill-payment-delete-${p.code}`}
                                                                onClick={() => deletePayment(p)}
                                                                className="text-xs text-rose-600 hover:underline"
                                                            >Delete</button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Documents */}
                    <div className="bg-white border border-slate-200 rounded-lg p-5">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium text-slate-900">Documents</h3>
                            {can("vendor:write") && (
                                <Button data-testid="vbill-upload-btn" size="sm" onClick={() => setDocOpen(true)} className="gap-1">
                                    <FileArrowUp size={14} /> Upload
                                </Button>
                            )}
                        </div>
                        {(bill.documents || []).length === 0 ? (
                            <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-md">
                                No documents attached
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {bill.documents.map((d) => (
                                    <div key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`vbill-doc-${d.id}`}>
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-slate-900 truncate">{d.file_name}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">
                                                <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100 mr-1">
                                                    {DOC_TYPE_LABEL[d.document_type] || d.document_type}
                                                </Badge>
                                                {humanBytes(d.size_bytes)} · uploaded {formatDate(d.uploaded_at)}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <a href={d.file_url} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                                                <DownloadSimple size={12} /> Download
                                            </a>
                                            {can("vendor:write") && (
                                                <Button size="sm" variant="ghost" onClick={() => removeDoc(d)}
                                                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 h-7 w-7 p-0">
                                                    <Trash size={14} />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {bill.notes && (
                        <div className="bg-white border border-slate-200 rounded-lg p-5">
                            <h3 className="font-medium text-slate-900 mb-3">Notes</h3>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{bill.notes}</p>
                        </div>
                    )}
                </div>

                <aside className="lg:col-span-1 space-y-4">
                    <div className="bg-white border border-slate-200 rounded-lg p-5">
                        <h3 className="font-medium text-slate-900 mb-3">Record metadata</h3>
                        <div className="space-y-2.5 text-xs">
                            <MetaRow label="Internal ID" value={bill.id} mono />
                            <MetaRow label="Display code" value={bill.code} mono />
                            <MetaRow label="Vendor Bill #" value={bill.bill_number} />
                            <MetaRow label="Vendor" value={`${bill.vendor_code} · ${bill.vendor_name}`} />
                            {bill.vendor_purchase_order_code && (
                                <MetaRow label="Source PO" value={bill.vendor_purchase_order_code} mono />
                            )}
                            <MetaRow label="Created" value={formatDate(bill.created_at)} />
                            <MetaRow label="Created by" value={bill.created_by_email} />
                            <MetaRow label="Updated" value={formatDate(bill.updated_at)} />
                        </div>
                    </div>

                    {bill.cancellation_reason && (
                        <div className="bg-rose-50 border border-rose-200 rounded-lg p-5">
                            <h3 className="font-medium text-rose-900 mb-2 text-sm">Cancellation Reason</h3>
                            <p className="text-xs text-rose-800">{bill.cancellation_reason}</p>
                        </div>
                    )}
                </aside>
            </div>

            <PaymentDialog
                key={payOpen ? (editPayment?.id || "new") : "closed"}
                open={payOpen}
                onClose={() => { setPayOpen(false); setEditPayment(null); }}
                onSaved={reload}
                bill={bill}
                payment={editPayment}
            />
            <DocumentDialog
                key={docOpen ? "open" : "closed"}
                open={docOpen}
                onClose={() => setDocOpen(false)}
                billId={id}
                onUploaded={reload}
            />
        </div>
    );
}

function PaymentDialog({ open, onClose, onSaved, bill, payment }) {
    const isEdit = !!payment;
    const outstanding = bill?.outstanding_amount || 0;
    const [form, setForm] = useState(() => ({
        payment_date: payment?.payment_date || todayISO(),
        amount: payment?.amount ?? outstanding,
        payment_mode: payment?.payment_mode || "NEFT",
        reference_number: payment?.reference_number || "",
        remarks: payment?.remarks || "",
    }));
    const [busy, setBusy] = useState(false);
    const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value }));

    const submit = async () => {
        if (!form.payment_date) { toast.error("Payment date required"); return; }
        if (Number(form.amount || 0) <= 0) { toast.error("Amount must be > 0"); return; }
        if (!PAYMENT_MODES.includes(form.payment_mode)) { toast.error("Choose a payment mode"); return; }
        setBusy(true);
        try {
            const payload = {
                payment_date: form.payment_date,
                amount: Number(form.amount),
                payment_mode: form.payment_mode,
                reference_number: form.reference_number,
                remarks: form.remarks,
            };
            if (isEdit) {
                await api.put(`/vendor-payments/${payment.id}`, payload);
                toast.success("Payment updated");
            } else {
                const r = await api.post(`/vendor-bills/${bill.id}/payments`, payload);
                toast.success(`Payment ${r.data.code} recorded`);
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="vbill-payment-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit Payment" : "Record Payment"}</DialogTitle>
                    <DialogDescription>
                        For bill {bill?.code} ({bill?.bill_number}). Outstanding: <strong>{formatAmount(outstanding, bill?.currency)}</strong>.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Field label="Payment Date *">
                        <Input data-testid="payment-date" type="date" value={form.payment_date} onChange={setField("payment_date")} />
                    </Field>
                    <Field label="Amount *">
                        <Input data-testid="payment-amount" type="number" min={0} step="0.01" value={form.amount} onChange={setField("amount")} />
                    </Field>
                    <Field label="Payment Mode *">
                        <select data-testid="payment-mode" value={form.payment_mode} onChange={setField("payment_mode")}
                            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm">
                            {PAYMENT_MODES.map((m) => (<option key={m} value={m}>{m}</option>))}
                        </select>
                    </Field>
                    <Field label="Reference Number">
                        <Input data-testid="payment-ref" value={form.reference_number} onChange={setField("reference_number")} placeholder="UTR / transaction ID / cheque no." />
                    </Field>
                    <Field label="Remarks">
                        <Textarea data-testid="payment-remarks" rows={2} value={form.remarks} onChange={setField("remarks")} />
                    </Field>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="payment-save-btn" onClick={submit} disabled={busy}>
                        {busy ? "Saving…" : isEdit ? "Update Payment" : "Record Payment"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DocumentDialog({ open, onClose, billId, onUploaded }) {
    const fileRef = useRef(null);
    const [docType, setDocType] = useState("invoice");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) { toast.error("Choose a file"); return; }
        if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("document_type", docType);
            await api.post(`/vendor-bills/${billId}/documents`, fd, { headers: { "Content-Type": undefined } });
            toast.success("Document uploaded");
            onUploaded();
            onClose();
            if (fileRef.current) fileRef.current.value = "";
        } catch (err) { toast.error(formatApiError(err, "Upload failed")); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="vbill-upload-dialog">
                <DialogHeader>
                    <DialogTitle>Upload Document</DialogTitle>
                    <DialogDescription>Vendor&apos;s invoice PDF, supporting docs. PDF/DOC/DOCX/PNG/JPG up to 10 MB.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <Field label="Document Type">
                        <select data-testid="vbill-upload-doctype" value={docType} onChange={(e) => setDocType(e.target.value)}
                            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm">
                            {Object.entries(DOC_TYPE_LABEL).map(([k, l]) => (<option key={k} value={k}>{l}</option>))}
                        </select>
                    </Field>
                    <Field label="File">
                        <input data-testid="vbill-upload-file" ref={fileRef} type="file"
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                            className="block w-full text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border file:border-slate-300 file:bg-white file:text-slate-700 file:cursor-pointer" />
                    </Field>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="vbill-upload-submit" onClick={submit} disabled={busy} className="gap-1">
                        <FileArrowUp size={14} /> {busy ? "Uploading…" : "Upload"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <Label className="text-xs text-slate-600 mb-1.5 inline-block">{label}</Label>
            {children}
            {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
        </div>
    );
}

function Stat({ label, value, tone, testid }) {
    const toneClass = tone === "positive" ? "text-emerald-700"
        : tone === "negative" ? "text-rose-700" : "text-slate-900";
    return (
        <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
            <div data-testid={testid} className={`mt-1 font-semibold font-mono ${toneClass}`}>{value}</div>
        </div>
    );
}

function MetaRow({ label, value, mono }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="text-slate-500 shrink-0">{label}</div>
            <div className={`text-slate-900 text-right break-all ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
        </div>
    );
}
